// g2-plan-gate-decision.ts
//
// G2 (P16). Spec §8: "G2 · Plan — Plan + ticket DAG + verification recipes +
// baseline exceptions và rủi ro thực tế. Mặc định bật nếu tổng ticket plan
// hiệu lực vượt 15, có high-impact/destructive migration, baseline exception
// hoặc đụng ranh giới scope. Mission nhỏ chỉ waived nếu G0 đã ủy nhiệm và
// semantic checks pass."
//
// Before this module G2 was decorative in BOTH directions — the same shape as
// Bug I/J: `waiveGate` had tests and ZERO production callers, and
// `g2_ticket_threshold` sat in the config schema read by nothing. So G2 was
// never requested and never waived; `gates.json` simply kept saying
// `not_requested` forever.
//
// SCOPE (D15, Luke chose option A). Only the conditions the CONTROLLER can
// measure from data it already owns are enforced:
//
//   ENFORCED   effective ticket count vs `g2_ticket_threshold` (config, so
//              §18's "no literal thresholds" rule holds)
//   ENFORCED   destructive/migration work — UNKNOWN in v1, so always tripped
//              (B2-5); `kind: "deploy"` stays as a defensive extra signal
//   ENFORCED   baseline exception — `baseline_exceptions[]` in the execution
//              repo's verification-profile (P17). Read through a high-water
//              mark because that file lives in the worker's worktree; see
//              g2-baseline-exception-mark.ts for why both lying directions
//              are unprofitable.
//   ENFORCED   scope boundary — `resource_names`, declared by the PERSON
//              running `avatar autopilot init` and persisted to the control
//              root by mission-declared-resources.ts (P25). Until P25 the
//              field had the Bug J shape: validated at init, then dropped, so
//              there was nothing to measure against. This deliberately uses a
//              source a worker CANNOT write — reaching for a worker-declared
//              ticket field like `risk` would hand the decision to the very
//              party the gate governs (the Bug K shape, where a worker
//              proposed `kind: "survey"` to dodge G0/G1; and Bug L, where it
//              cancelled tickets to walk the plan back under the threshold).
//
// `unenforcedConditions` is now empty, and the machinery that reports it is
// KEPT ON PURPOSE: it is what stops a future §8 condition from being added to
// the spec and silently treated as "checked and clear". Emptying the list is
// the goal; deleting the channel would be re-opening the hole.
//
// WHY an undeclared boundary REQUIRES the gate rather than passing it: a
// missing boundary is not a permissive one. `undefined` (nobody declared —
// a mission predating P25, or an unreadable file) and `[]` (the person
// declared no external resources) BOTH send the plan to a human, but with
// different stated reasons, because collapsing two real states into one
// indistinguishable outcome is the swallowed-state bug this project has paid
// for three times (P20 Lỗi A, P21 promote result, P24 release flow outcome).
import { effectivePlanTickets } from "./g2-effective-plan-tickets.js";
import type { Ticket } from "./schemas/ticket-schema.js";

// "Effective plan" ticket filtering split into g2-effective-plan-tickets.ts
// to keep this file ≤200 lines — re-exported so callers only import ONE
// module for "G2 plan decision".
export { countEffectivePlanTickets } from "./g2-effective-plan-tickets.js";

/**
 * Spec conditions this module cannot measure yet — surfaced so callers never
 * read "not required" as "all four cleared". Empty since P25 closed
 * `scope_boundary`, the last one. KEEP the channel: a §8 condition added
 * later must land here rather than being quietly assumed clear, which is
 * exactly how `scope_boundary` sat unmeasured for eight phases.
 */
const UNENFORCED_CONDITIONS: readonly string[] = [];

export type G2UnenforcedCondition = string;

export interface G2DecisionInput {
  tickets: Ticket[];
  /** Ticket id -> runtime status; absent id means the ticket is still effective. */
  ticketStatus: Record<string, string>;
  /** From effective-config (`g2_ticket_threshold`) — never a literal here. */
  g2TicketThreshold: number;
  /** From effective-config (`g2_waive_delegated_by_g0`): did the G0 approver delegate the G2 decision? */
  g0Delegated: boolean;
  /**
   * Bug L (D16): largest effective plan size ever observed for this mission.
   * Without it the count condition is gameable — cancelling one ticket out of
   * 16 drops a plan back under the threshold and flips G2 from required to
   * waivable. Defaults to 0 (inert) so callers predating this field behave
   * exactly as before.
   */
  planSizeHighWaterMark?: number;
  /**
   * P17: most `baseline_exceptions` ever observed in the execution repo's
   * verification-profile. A peak, not the live count, for the same reason
   * `planSizeHighWaterMark` is — the file sits in the worker's worktree.
   * Defaults to 0 (inert) so callers predating this field behave as before.
   */
  baselineExceptionHighWaterMark?: number;
  /**
   * P25 §8 condition 4. The resource boundary declared by the person at
   * `autopilot init` (`resource_names`), read back from the control root.
   * `undefined` means UNKNOWN — no declaration was ever persisted — and is
   * NOT the same as a declared empty list; both require the gate, for
   * different reasons the caller can read in `reasons`.
   */
  declaredResourceNames?: readonly string[];
}

export interface G2Decision {
  /** true => G2 must be requested from a human before feature dispatch continues. */
  required: boolean;
  /** true => the mission is small enough AND G0 delegated, so the controller may record a waive. */
  waivable: boolean;
  /** Human-readable, safe to journal/print — why the gate is (or is not) required. */
  reasons: string[];
  unenforcedConditions: readonly G2UnenforcedCondition[];
}

/**
 * Decides whether G2 is required for this plan, and whether a waive is
 * permitted. Pure function over already-loaded state — no I/O, so the caller
 * controls when and how the result is persisted.
 */
export function decideG2Requirement(input: G2DecisionInput): G2Decision {
  const planTickets = effectivePlanTickets(input.tickets, input.ticketStatus);
  const reasons: string[] = [];

  // Spec: "vượt 15" — strictly greater, so exactly at the threshold is fine.
  // Bug L (D16): judged against the PEAK size ever observed, not just the
  // live one, so cancelling tickets cannot walk a plan back under the
  // threshold. `Math.max` keeps this monotonic even before the mark is first
  // written (it defaults to 0).
  const peakPlanSize = Math.max(planTickets.length, input.planSizeHighWaterMark ?? 0);
  if (peakPlanSize > input.g2TicketThreshold) {
    const observed =
      peakPlanSize === planTickets.length
        ? `${planTickets.length} ticket hiệu lực`
        : `${planTickets.length} ticket hiệu lực (đỉnh từng đạt ${peakPlanSize})`;
    reasons.push(
      `plan có ${observed}, vượt ngưỡng ${input.g2TicketThreshold} (g2_ticket_threshold).`,
    );
  }

  const deployTickets = planTickets.filter((t) => t.kind === "deploy");
  if (deployTickets.length > 0) {
    reasons.push(
      `plan có ${deployTickets.length} ticket kind "deploy" (việc destructive/migration theo §8): ` +
        `${deployTickets.map((t) => t.id).join(", ")}.`,
    );
  }

  const baselineExceptions = input.baselineExceptionHighWaterMark ?? 0;
  if (baselineExceptions > 0) {
    reasons.push(
      `plan chạy trên baseline có ${baselineExceptions} baseline exception đã chấp nhận ` +
        "(verification-profile.baseline_exceptions, §8) — người duyệt cần nhìn plan trên nền test đỏ đã được miễn trừ.",
    );
  }

  // §8 condition 4. Judged on the PERSON's declaration, never on anything a
  // worker writes. Both branches below block the waive path: a plan whose
  // resource boundary is unknown or empty is not a "mission nhỏ" whose scope
  // G0 can have delegated away in advance.
  if (input.declaredResourceNames === undefined) {
    reasons.push(
      "mission chưa khai ranh giới tài nguyên (resource_names ở init.schema, §8) — " +
        "không có ranh giới để đối chiếu plan, cần người duyệt.",
    );
  } else if (input.declaredResourceNames.length === 0) {
    reasons.push(
      "mission không khai tài nguyên ngoài repo nào (resource_names rỗng, §8) — " +
        "người duyệt cần xác nhận plan không đụng tài nguyên ngoài ranh giới đó.",
    );
  }

  // B2-5 (design H4/H14 bản 2, 2026-09-29): v1 has no trustworthy
  // declaration of destructive/migration work — the one structural signal,
  // `kind: "deploy"`, can no longer enter the DAG (H14). Unknown is not
  // "none", so the condition is treated as tripped: G2 is always required
  // and G0 delegation cannot waive it (D-G1 a). A destructive/migration
  // declaration in the BLUEPRINT, bound by G0, is the backlog item that
  // would make a waive possible again (D-G1 b).
  reasons.push(
    "chưa xác định được việc destructive/migration (v1 chưa có khai báo đáng tin, §8) — " +
      "G2 luôn cần người duyệt" +
      (input.g0Delegated ? "; g2_waive_delegated_by_g0=true không còn waive được G2 ở v1." : "."),
  );

  const triggeredByPlan = reasons.length > 0;

  // Spec: "Mission nhỏ CHỈ waived nếu G0 đã ủy nhiệm". A plan that tripped any
  // condition above is not a "mission nhỏ", so delegation cannot cover it.
  const waivable = !triggeredByPlan && input.g0Delegated;

  return {
    required: triggeredByPlan || !input.g0Delegated,
    waivable,
    reasons,
    unenforcedConditions: UNENFORCED_CONDITIONS,
  };
}
